import configureAxios from '../config/axios';
import pickBy from 'lodash/pickBy';
import store from '../config/store';
import { loginCheck } from '../actions/login';
import { BIG_PAGE_SIZE } from '../constants';

const axios = configureAxios();
axios.interceptors &&
  axios.interceptors.response.use(
    (data) => data,
    (error) => {
      if (error.response && error.response.status === 401) {
        const { login } = store.getState();

        if (login.loggedIn) {
          loginCheck()(store.dispatch);
        }
      }

      return Promise.reject(error);
    },
  );

export const requestLogin = (
  email,
  password,
  recaptchaToken,
  deviceUuid,
  verificationCode,
  code_sending_mode,
  target_mdn,
) =>
  axios.post('users/login/', {
    email,
    password,
    recaptcha_token: recaptchaToken,
    device_uid: deviceUuid,
    device_verification_code: verificationCode,
    code_sending_mode,
    target_mdn,
  });

export const logout = () => axios.post('users/logout/');

export const fetchLoggedInUser = () => axios.get('users/me/');

// Shippings
export const fetchShippingEstimate = (payload = {}) =>
  axios.post(`shippings/estimate_express_shipping/`, payload);
export const editShipping = (id, attrs) => axios.put(`shippings/${id}/`, attrs);

// Users
export const fetchAllUsers = (
  page = 1,
  childAccounts,
  userId,
  ordering = '-id',
) => {
  if (childAccounts) {
    return axios.get(
      `users/?page=${page}&ordering=${ordering}&parent=${userId}`,
    );
  } else {
    return axios.get(`users/?page=${page}&ordering=${ordering}`);
  }
};
export const findUser = (id) => axios.get(`users/${id}/`);
export const fetchUsersApiOptions = () => axios.options(`users/`);
export const saveUser = (id, attrs) => axios.put(`users/${id}/`, attrs);
export const saveUserField = (id, attrs) => axios.patch(`users/${id}/`, attrs);
export const saveUserPatch = (id, attrs) => axios.patch(`users/${id}/`, attrs);
export const updateUserCreator = (id, attrs) =>
  axios.post(`users/${id}/update_creator/`, attrs);
export const toggleUserAutopay = (id, status) =>
  axios.post(`users/${id}/autopay/`, { enabled: status });
export const toggleUserFeeExempt = (id, status) =>
  axios.post(`users/${id}/plan_fee_exempt/`, { enabled: status });
export const toggleProcessingFeeExempt = (id, status) =>
  axios.post(`users/${id}/processing_fee_exempt/`, { enabled: status });
export const toggleUserIsActive = (id, status) =>
  axios.post(`users/${id}/is_active/`, { enabled: status });
export const setTaxExempt = (id, taxExempt) =>
  axios.post(`users/${id}/tax_exempt/`, { enabled: taxExempt });
export const updateSimoAccounts = (userId, attrs) =>
  axios.post(`users/${userId}/update_simo_accounts/`, attrs);
export const fetchUserSubCounts = (userId) =>
  axios.get(`users/${userId}/subscription_counts/`);
export const createUser = (attrs) => axios.post('users/', attrs);
export const searchUsers = (page = 1, search) => {
  return axios.get(`users/`, {
    params: {
      only_active: false,
      page,
      ...search,
    },
  });
};
export const sendSubsCsv = (id, attrs) =>
  axios.post(`users/${id}/send_subscriptions_report/`, attrs);
export const fetchLoginDevices = (params) =>
  axios.get('users/two_fa_devices/', { params });
export const updateSimoCycleType = (id, simo_cycle_type) =>
  axios.post(`users/${id}/update_simo_cycle_type/`, { simo_cycle_type });

export const fetchUserPaymentMethods = (id, page = 1) =>
  axios.get(`users/${id}/card_info/?page=${page}`);
export const createPaymentMethod = (id, attrs) =>
  axios.post(`users/${id}/card_info/`, attrs);
export const createECheckPaymentMethod = (id, attrs) =>
  axios.post(`users/${id}/add_echeck/`, attrs);
export const setDefaultPaymentMethod = (id) =>
  axios.post(`payment_methods/${id}/set_default/`);
export const deletePaymentMethod = (userId, id) =>
  axios.delete(`users/${userId}/card_info/?card_id=${id}`);
export const updatePaymentProfile = (userId, attrs) =>
  axios.post(`users/${userId}/update_authnet_payment_profile/`, attrs);

export const fetchUserCredit = (id, page = 1) =>
  axios.get(`credits/?user_profile=${id}&page=${page}`);
export const fetchCreditsOptions = () => axios.options(`credits/`);
export const findCredit = (id) => axios.get(`credits/${id}/`);
export const createCredit = (attrs) => axios.post(`credits/`, attrs);
export const deleteCredit = (id) => axios.delete(`credits/${id}/`);
export const cancelCredit = (id) => axios.post(`credits/${id}/cancel/`);
export const useCredit = ({ purchaseId, creditId }) =>
  axios.post(`purchases/${purchaseId}/use_credit/`, { credit_id: creditId });
export const refundAffirm = (purchaseId, payload) =>
  axios.post(`purchases/${purchaseId}/refund/`, payload);
export const refundPurchase = (purchaseId, payload) =>
  axios.post(`purchases/${purchaseId}/refund/`, payload);
export const massPurchaseCharge = (payload) =>
  axios.post(`purchases/mass_charge/`, payload);

export const upgradePurchaseAttPlan = (payload) =>
  axios.post(`purchases/att_plan/`, payload);

export const setPurchaseOverdue = (id) =>
  axios.post(`purchases/${id}/checkout/`, {
    capture: false,
    set_overdue: true,
    no_auto_close: true,
  });
export const setPurchaseOpen = (id) =>
  axios.post(`purchases/${id}/checkout/`, {
    set_open: true,
  });
export const setPurchaseDueDate = (id, attrs) =>
  axios.post(`purchases/${id}/set_due_date/`, attrs);
export const allocatePurchaseLineitems = (id, attrs) =>
  axios.post(`purchases/${id}/allocate/`, attrs);
export const setPurchaseLocked = (id, attrs) =>
  axios.post(`purchases/${id}/toggle_purchase_lock/`, attrs);
export const setPurchasePaymentArrangementOverdue = (id) =>
  axios.post(`purchases/${id}/payment_arrangement_approval/`, {
    status: 'overdue',
  });
export const completeWithAch = (id) =>
  axios.post(`purchases/${id}/ach_payment/`);
export const cancelPurchase = (id) => axios.post(`purchases/${id}/cancel/`);
export const mergePurchases = (id, attrs) =>
  axios.post(`purchases/${id}/merge/`, attrs);
export const purchaseMassAction = (attrs) =>
  axios.post(`purchases/mass_payment_arrangement_approval/`, attrs);
export const addItemToPurchase = (attrs, purchaseId) =>
  axios.post(`purchases/${purchaseId}/add_item/`, attrs);
export const beginOffloadPurchase = (attrs, purchaseId) =>
  axios.post(`purchases/${purchaseId}/begin_offload/`, attrs);
export const recalculateShipping = (attrs, purchaseId) =>
  axios.post(`purchases/${purchaseId}/recalculate_shipping/`, attrs);
export const cancelOffloadPurchase = (id) =>
  axios.post(`purchases/${id}/cancel_offload/`);
export const createPaymentArrangements = (id, values) =>
  axios.post(`purchases/${id}/payment_arrangement/`, values);
export const approvePaymentArrangement = (id, status) =>
  axios.post(`purchases/${id}/payment_arrangement_approval/`, { status });
export const searchPurchases = (page = 1, search) => {
  return axios.get(`purchases/`, {
    params: {
      ...search,
    },
  });
};
export const transferLineItems = (lineitems, from, to_purchase, user_profile) =>
  axios.post(`purchases/${from}/transfer_item/`, {
    to_other_user: user_profile,
    lineitems,
    to_purchase,
  });
export const fetchPendingArrangements = (page, search) =>
  axios.get(`purchases/`, { params: { ...search, page } });

export const fetchUserPurchases = (id, page = 1, params = {}) =>
  axios.get(`purchases/?user_profile=${id}&page=${page}`, { params });
export const generateBlankInvoice = (id) =>
  axios.post(`purchases/`, { user_profile: id });
export const fetchOverduePurchases = (user_profile) =>
  axios.get(`purchases/`, {
    params: {
      user_profile,
      status: 'overdue',
    },
  });

export const fetchOrders = (page, filters) =>
  axios.get(`orders/`, { params: { ...filters, page } });
export const findOrderById = (orderId) => axios.get(`orders/${orderId}/`);
export const confirmOrder = (orderId, payload) =>
  axios.post(`orders/${orderId}/confirm/`, payload);
export const findPurchaseOrders = (purchaseId, page) =>
  axios.get(`orders/?purchase=${purchaseId}&page=${page}`);

export const fetchUserOverduePurchases = (id) =>
  axios.get(`purchases/?user_profile=${id}&status=overdue`);
export const fetchUserCanceledPurchases = (id, page = 1, params = {}) =>
  axios.get(`purchases/?user_profile=${id}&status=cancelled&page=${page}`, {
    params,
  });
export const findPurchase = (id) => axios.get(`purchases/${id}/`);
export const fetchPurchaseLineItems = (id, page = 1, filters) =>
  axios.get(`purchases/${id}/lineitems/`, { params: { ...filters, page } });
export const fetchPurchaseWithAllLineItems = (id, page, filters, page_size) =>
  axios.get(`purchases/${id}/lineitems/`, {
    params: { ...filters, page, page_size },
  });
export const fetchPurchaseTaxes = (id) => axios.get(`purchases/${id}/tax/`);

export const deletePurchase = (id) => axios.delete(`purchases/${id}/`);
export const deleteLineItemById = (id, params) =>
  axios.post(`purchases/${id}/remove_item/`, params);
export const deleteLineItemsById = (id, params) =>
  axios.post(`purchases/${id}/mass_remove_item/`, params);
export const partialRefund = (id, lineItemsToRefund) =>
  axios.post(`purchases/${id}/partial_refund/`, lineItemsToRefund);
export const createLineItem = (id, attrs) =>
  axios.post(`purchases/${id}/add_item/`, attrs);
export const sendPurchaseInvoice = (id) =>
  axios.post(`purchases/${id}/send_email/`);
export const chargeCard = (purchaseId, cardId, cvc) =>
  axios.post(
    `purchases/${purchaseId}/checkout/`,
    cvc
      ? {
          card_id: cardId,
          capture: 'true',
          cvc,
        }
      : {
          card_id: cardId,
          capture: 'true',
        },
  );
export const mergePurchase = (purchaseId, payload) =>
  axios.post(`purchases/${purchaseId}/merge/`, payload);
export const splitPayment = (purchaseId, amount) =>
  axios.post(`purchases/${purchaseId}/split/`, {
    total_amount: amount,
  });
export const generateInvoice = (id, use_credit) =>
  axios.post('purchases/renewal/', {
    user_profile: id,
    use_credit,
  });
export const createPurchase = (user_profile) =>
  axios.post('purchases/', {
    user_profile,
  });

export const setShippingOption = (purchaseId, attrs) =>
  axios.post(`purchases/${purchaseId}/shipping_options/`, attrs);

export const createPurchaseItem = (purchaseId, item_id, item_type) =>
  axios.post(`purchases/${purchaseId}/add_item/`, {
    item_id,
    item_type,
  });
export const fetchLogs = (params, page = 1) => {
  return axios.get(`logs/events/?page=${page}`, { params });
};

export const fetchSubLeases = (subId, page = 1, params) =>
  axios.get(`leases/?subscription=${subId}&page=${page}`, {
    params,
  });
export const fetchAttSubLeases = (subId, page = 1, params) =>
  axios.get(`leases/?att_subscription=${subId}&page=${page}`, {
    params,
  });
export const findLease = (id) => axios.get(`leases/${id}/`);
export const fetchLeases = (page = 1, params) =>
  axios.get(`leases/?page=${page}`, { params });
export const createLease = (attrs) => axios.post('leases/', attrs);
export const closeLease = (id) => axios.post(`leases/${id}/close_out/`);
export const transferLease = (id, params) =>
  axios.post(`leases/${id}/transfer/`, params);
export const updateLease = (id, attrs, method) => {
  if (method === 'PATCH') {
    return axios.patch(`leases/${id}/`, attrs);
  } else {
    return axios.put(`leases/${id}/`, attrs);
  }
};

export const createAddress = (attrs) => axios.post('addresses/', attrs);
export const saveAddress = (id, attrs) => axios.put(`addresses/${id}/`, attrs);

export const fetchUserLogs = (id, filters = {}, page) => {
  return axios.get(`logs/events/`, {
    params: { ...filters, page, owning_user: id },
  });
};
export const getLogSuggestions = (filter) =>
  axios.get(`event_types/?q=${filter}`);
export const getLogsModels = () => axios.get(`logs/events/models/`);

export const createNote = (attrs) => axios.post('notes/', attrs);
export const fetchNotes = (payload, page) =>
  axios.get(`notes/`, { params: { ...payload, page } });
export const deleteNote = (id) => axios.delete(`/notes/${id}/`);
export const updateNote = (id, attrs) => axios.patch(`/notes/${id}/`, attrs);

export const fetchAllAttSubs = (page = 1, ordering = '-id') =>
  axios.get(`att/subscriptions/?page=${page}&ordering=${ordering}`);
export const findAttSub = (id) => axios.get(`att/subscriptions/${id}/`);
export const searchAttSubs = (page = 1, search, page_size) => {
  return axios.get(`att/subscriptions/`, {
    params: {
      ...search,
      page,
      page_size,
    },
  });
};

export const suspendAttSubscription = (id, suspensionReason, expiryType) =>
  axios.post(`att/subscriptions/${id}/suspend/`, {
    suspension_status: suspensionReason,
    expiry_type: expiryType,
  });
export const suspendAttSubscriptionOptions = (id) =>
  axios.options(`att/subscriptions/${id}/suspend/`);
export const attMassSuspend = (attrs) =>
  axios.post(`att/subscriptions/mass_suspend/`, attrs);
export const attMassExpire = (attrs) =>
  axios.post(`att/subscriptions/mass_expire/`, attrs);
export const attMassDataBlock = (attrs) =>
  axios.post(`att/subscriptions/mass_data_block/`, attrs);
export const attMassTransfer = (attrs) =>
  axios.post(`att/subscriptions/mass_transfer/`, attrs);

export const restoreAttSubscription = (id) =>
  axios.post(`att/subscriptions/${id}/restore/`);

export const transferAttSubscription = (id, attrs) =>
  axios.post(`att/subscriptions/${id}/transfer/`, attrs);
export const transferTracfoneSubscription = (id, attrs) =>
  axios.post(`tracfone/subscriptions/${id}/transfer/`, attrs);

export const fetchAllSubs = (page = 1, ordering = '-id') =>
  axios.get(`subscriptions/?page=${page}&ordering=${ordering}`);
export const findSub = (id) => axios.get(`subscriptions/${id}/`);
export const createSub = (attrs) => axios.post(`subscriptions/`, attrs);
export const createAttSub = (attrs) => axios.post(`att/subscriptions/`, attrs);
export const updateSub = (id, attrs) =>
  axios.patch(`subscriptions/${id}/`, pickBy(attrs));
export const updateAttSub = (id, attrs) =>
  axios.patch(`att/subscriptions/${id}/`, attrs);
export const fetchUserSubs = (userId, page = 1) => {
  return userId
    ? axios.get(`subscriptions/?user_profile=${userId}&page=${page}`)
    : axios.get(`subscriptions/?page=${page}`);
};
export const fetchUserAttSubs = (userId, page = 1) => {
  return userId
    ? axios.get(`att/subscriptions/?user_profile=${userId}&page=${page}`)
    : axios.get(`att/subscriptions/?page=${page}`);
};
export const fetchUserFilteredSubs = (userId, filters, page = 1) => {
  let url = '';
  if (userId) {
    url = `subscriptions/?user_profile=${userId}&page=${page}`;
  } else {
    url = `subscriptions/?page=${page}`;
  }
  if (filters) {
    url += `&${filters}`;
  }
  return axios.get(url);
};
export const fetchUserAttFilteredSubs = (userId, filters, page = 1) => {
  let url = '';
  if (userId) {
    url = `att/subscriptions/?user_profile=${userId}&page=${page}`;
  } else {
    url = `att/subscriptions/?page=${page}`;
  }
  if (filters) {
    url += `&${filters}`;
  }
  return axios.get(url);
};
export const fetchAllInitialsSubs = (page, params, page_size) =>
  axios.get(`subscriptions/?page=${page}`, {
    params: { ...params, page_size },
  });
export const fetchAllAttInitialsSubs = (page, params, page_size) =>
  axios.get(`att/subscriptions/?page=${page}`, {
    params: { ...params, page_size },
  });
export const fetchAllTracfoneInitialsSubs = (page, params, page_size) =>
  axios.get(`tracfone/subscriptions/?page=${page}`, {
    params: { ...params, page_size },
  });
export const fetchAllSimoInitialsSubs = (page, params, page_size) =>
  axios.get(`simo/subscriptions/?page=${page}`, {
    params: { ...params, page_size },
  });

export const fetchServiceCodes = (id) =>
  axios.get(`subscriptions/${id}/service_codes/`);
export const findSubDataUsage = (id, cycle) =>
  axios.get(`usage/${id}/?cycle=${cycle}`);
export const findSubVoiceUsage = (id, cycle) =>
  axios.get(`calls/usage/${id}/?cycle=${cycle}`);
export const findSubRoamingUsage = (id, cycle) =>
  axios.get(`roaming/usage/${id}/?cycle=${cycle}`);
export const findSubDailyUsage = (id, cycle = 'current') =>
  axios.get(`usage/${id}/daily/?cycle=${cycle}`);
export const searchSubs = (page = 1, search, page_size) => {
  return axios.get(`subscriptions/`, {
    params: {
      ...search,
      page,
      page_size,
    },
  });
};

export const expireAttSubscription = (id, attrs) =>
  axios.post(`att/subscriptions/${id}/expire/`, attrs);
export const expireTracfoneSubscription = (id, attrs) =>
  axios.post(`tracfone/subscriptions/${id}/expire/`, attrs);
export const expireSimoSubscription = (id, attrs) =>
  axios.post(`simo/subscriptions/${id}/expire/`, attrs);
export const simoMassSyncActivation = (attrs) =>
  axios.post(`simo/subscriptions/mass_sync_activation/`, attrs, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
export const simoMassActivation = (attrs) =>
  axios.post(`simo/subscriptions/mass_activation/`, attrs, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const findPreactivation = (id) => axios.get(`preactivations/${id}/`);
export const fetchUserPreactivations = (userId) =>
  axios.get(`preactivations/?user_profile=${userId}`);

// Sprint Porting & Activation
export const validatePort = (mdn) =>
  axios.get(`sprint/validate_port/?mdn=${mdn}`);
export const updateDevice = (id, device) =>
  axios.post(`subscriptions/${id}/update_device/`, device);
export const updatePort = (id, port) =>
  axios.post(`subscriptions/${id}/update_port/`, port);
export const portCancel = (id, data) =>
  axios.post(`subscriptions/${id}/port_cancel/`, data);
export const portModify = (id, port) =>
  axios.post(`subscriptions/${id}/port_modify/`, port);
export const portInfo = (id) => axios.get(`subscriptions/${id}/port_info/`);
export const activateSub = (id, data) =>
  axios.post(`subscriptions/${id}/activate/`, data);

// AT&T Porting & Activation
export const validateAttDevice = (device_id) =>
  axios.get(`att/device_eligibility/`, { params: { device_id } });
export const validateAttPort = (mdn, service_zip_code) =>
  axios.get(`att/validate_port/`, { params: { mdn, service_zip_code } });
export const updateAttDevice = (id, device) =>
  axios.post(`att/subscriptions/${id}/update_device/`, device);
export const activateAttSub = (id, data) =>
  axios.post(`att/subscriptions/${id}/activate/`, data);
export const updateAttMdn = (id, data) =>
  axios.post(`att/subscriptions/${id}/update_mdn/`, data);

// Subscription Plans
export const updatePlan = (id, plan) =>
  axios.post(`subscriptions/${id}/update_plan/`, plan);
export const setUpcomingAttPlan = (subscription, { plan_id, plan_lock }) =>
  axios.post(`att/cycles/upcoming/`, {
    plan: plan_id,
    plan_lock,
    subscription,
  });

export const fetchSprintCurrentCycle = (subscription) =>
  axios.get('/cycles/current/', {
    params: {
      subscription,
    },
  });
export const fetchAttCurrentCycle = (subscription) =>
  axios.get('/att/cycles/current/', {
    params: {
      subscription,
    },
  });
export const fetchSimoCurrentCycle = (subscription) =>
  axios.get('/simo/cycles/current/', {
    params: {
      subscription,
    },
  });
export const fetchCycles = () => axios.get('/cycles/');
export const fetchSubUpcomingPlan = (sub) =>
  axios.get(`/cycles/upcoming/`, { params: { subscription: sub } });
export const updateAttPlan = (id, plan) =>
  axios.post(`att/subscriptions/${id}/update_plan/`, plan);
export const lockAttPlan = (id, locked) =>
  axios.put(`att/subscriptions/${id}/plan_locked/`, { plan_locked: locked });
export const fetchAttSubUpcomingPlan = (sub) =>
  axios.get(`att/cycles/upcoming/`, { params: { subscription: sub } });
export const fetchPlansByPage = (page = 1, params) =>
  axios.get(`plans/`, { params: { ...params, page } });
export const fetchAllPlans = async (params) => {
  let page = 1;
  let isKeepOn = true;
  const response = [];

  while (isKeepOn) {
    const plansResponse = await fetchPlansByPage(page, params);
    if (Array.isArray(plansResponse.data.results)) {
      response.push(...plansResponse.data.results);
    }

    if (plansResponse.data.next) {
      page++;
    } else {
      isKeepOn = false;
    }
  }

  return response;
};
export const fetchAttPlansByPage = (page = 1, params, page_size) =>
  axios.get(`att/plans/?ordering=plan_carrier,data_available,name,price`, {
    params: { ...params, page, page_size },
  });
export const fetchAllAttPlans = async (params) => {
  let page = 1;
  let isKeepOn = true;
  const response = [];

  while (isKeepOn) {
    const plansResponse = await fetchAttPlansByPage(
      page,
      params,
      BIG_PAGE_SIZE,
    );
    if (Array.isArray(plansResponse.data.results)) {
      response.push(...plansResponse.data.results);
    }

    if (plansResponse.data.next) {
      page++;
    } else {
      isKeepOn = false;
    }
  }

  return response;
};

export const fetchPlan = (id) => axios.get(`plans/${id}/`);
export const fetchAttPlan = (id) => axios.get(`att/plans/${id}/`);

export const fetchAllServiceCodes = () => axios.get('plans/service_codes/');

export const findInsurancePlan = (id) => axios.get(`/insurance/plans/${id}/`);

export const fetchDevicesByPage = (page = 1, page_size) =>
  axios.get(`devices/?page=${page}`, { params: { page_size } });
export const findDevice = (sku) => axios.get(`devices/${sku}/`);
export const fetchAllDevices = async () => {
  let page = 1;
  let isKeepOn = true;
  const response = [];

  while (isKeepOn) {
    const deviceResponse = await fetchDevicesByPage(page, BIG_PAGE_SIZE);
    if (Array.isArray(deviceResponse.data.results)) {
      response.push(...deviceResponse.data.results);
    }

    if (deviceResponse.data.next) page++;
    else isKeepOn = false;
  }
  return response;
};

export const fetchAllInsuranceContracts = (page = 1, filters = {}) =>
  axios.get(`insurance/contracts/`, {
    params: {
      ordering: '-created_at',
      page,
      ...filters,
    },
  });
export const fetchUserInsuranceContracts = (user_profile) =>
  axios.get('insurance/contracts/', { params: { user_profile: user_profile } });
export const fetchSprintSubContract = (subscription) =>
  axios.get('insurance/contracts/', { params: { subscription: subscription } });
export const fetchAttSubContract = (att_subscription) =>
  axios.get('insurance/contracts/', {
    params: { att_subscription: att_subscription },
  });
export const fetchAllInsuranceDevices = () => axios.get('insurance/devices/');
export const fetchDeviceByContract = (contract) =>
  axios.get('insurance/devices/', { params: { contract: contract } });
export const createInsuranceContract = (contract) =>
  axios.post('insurance/contracts/', contract);
export const createInsuranceDevice = (device) =>
  axios.post('insurance/devices/', device);
export const fetchInsuranceContract = (id) =>
  axios.get(`insurance/contracts/${id}/`);
export const fetchInsuranceDevice = (id) =>
  axios.get(`insurance/devices/${id}/`);
export const updateInsuranceContract = (id, contract) =>
  axios.put(`insurance/contracts/${id}/`, contract);
export const updateInsuranceDevice = (id, device) =>
  axios.put(`insurance/devices/${id}/`, device);
export const activateInsuranceContract = (id) =>
  axios.post(`insurance/contracts/${id}/activate/`);
export const activateInsuranceDevice = (id) =>
  axios.post(`insurance/devices/${id}/activate/`);
export const cancelInsuranceContract = (id, cancel) =>
  axios.post(`insurance/contracts/${id}/cancel/`, cancel);
export const cancelInsuranceDevice = (id, cancel) =>
  axios.post(`insurance/devices/${id}/cancel/`, cancel);

// Sprint calls
export const sendSprintCallRecords = (data) => axios.post(`calls/logs/`, data);

// Verification codes
export const sendAttVerificationCode = (id, attrs) =>
  axios.post(`att/subscriptions/${id}/request_auth_code/`, attrs);
export const switchSprintToAtt = (id, attrs) =>
  axios.post(`subscriptions/${id}/start_switch_to_att/`, attrs);
export const confirmSwitchSprintToAtt = (id, attrs) =>
  axios.post(`subscriptions/${id}/confirm_switch_to_att/`, attrs);
export const verifyAttVerificationCode = (id, attrs) =>
  axios.post(`att/subscriptions/${id}/verify_auth_code/`, attrs);
export const switchAttToSprint = (id, attrs) =>
  axios.post(`att/subscriptions/${id}/start_switch_to_sprint/`, attrs);
export const confirmSwitchAttToSprint = (id, attrs) =>
  axios.post(`att/subscriptions/${id}/confirm_switch_to_sprint/`, attrs);

// Device contracts
export const findDeviceContract = (id) => axios.get(`devices/contracts/${id}/`);
export const fetchUserDeviceContracts = (userId, page = 1) =>
  axios.get(`devices/contracts/`, { params: { user_profile: userId, page } });
export const settleDeviceContract = (id, attrs) =>
  axios.put(`devices/contracts/${id}/`, attrs);
export const fetchPurchaseContractByUserId = async (userId, purchaseId) => {
  let page = 1;
  let fetchedData = [];
  while (true) {
    const { data } = await fetchUserDeviceContracts(userId, page);
    fetchedData.push(
      ...data.results.filter((contract) => contract.purchase === purchaseId),
    );

    if (!data.next) break;

    page++;
  }

  return fetchedData;
};

// Rolling payment arrangements
export const findRollingArrangement = (id) =>
  axios.get(`rolling_payments/${id}/`);
export const fetchRollingArrangements = (search, page) =>
  axios.get(`rolling_payments/`, { params: { ...search, page } });
export const fetchUserRollingArrangements = (userId) =>
  axios.get(`rolling_payments/?user_profile=${userId}`);
export const createRollingArrangement = (attrs) =>
  axios.post(`/rolling_payments/`, attrs);
export const editRollingArrangement = (id, attrs) =>
  axios.put(`/rolling_payments/${id}/`, attrs);

// Custom Plan Pricing
export const fetchCustomPlanPricing = (id) =>
  axios.get(`custom_plan_prices/${id}/`);
export const fetchCustomPlanPricingList = (page = 1, search, pageSize) =>
  axios.get(`custom_plan_prices/`, {
    params: { ...search, page, page_size: pageSize },
  });
export const createCustomPlanPricing = (attrs) =>
  axios.post('custom_plan_prices/', attrs);
export const updateCustomPlanPricing = (planId, attrs) =>
  axios.patch(`custom_plan_prices/${planId}/`, attrs);
export const deleteCustomPlanPricing = (planId) =>
  axios.delete(`custom_plan_prices/${planId}/`);

// fetch data and allow resellers to toggle suspend status for subscriptions
export const fetchAndAllowResellerToggleStatus = (carrier, subId) =>
  axios.get(`/purchases/?status=overdue&${carrier}=${subId}`);

// tracfone
export const fetchTracfonePlansByPage = (page = 1, params) =>
  axios.get(`tracfone/plans/`, { params: { ...params, page } });
export const fetchAllTracfonePlans = async (params) => {
  let page = 1;
  let isKeepOn = true;
  const response = [];

  while (isKeepOn) {
    const plansResponse = await fetchTracfonePlansByPage(page, params);
    if (Array.isArray(plansResponse.data.results)) {
      response.push(...plansResponse.data.results);
    }

    if (plansResponse.data.next) {
      page++;
    } else {
      isKeepOn = false;
    }
  }

  return response;
};
export const fetchTracfonePlan = (id) => axios.get(`tracfone/plans/${id}/`);
export const fetchAllTracfoneSubs = (page = 1, ordering = '-id') =>
  axios.get(`tracfone/subscriptions/?page=${page}&ordering=${ordering}`);
export const updateTracfonePlan = (id, plan) =>
  axios.post(`tracfone/subscriptions/${id}/update_plan/`, plan);
export const createTracfoneSub = (attrs) =>
  axios.post(`tracfone/subscriptions/`, attrs);
export const updateTracfoneSub = (id, attrs) =>
  axios.patch(`tracfone/subscriptions/${id}/`, attrs);
export const activateTracfoneSub = (id, data) =>
  axios.post(`tracfone/subscriptions/${id}/activate/`, data);
export const findTracfoneSub = (id) =>
  axios.get(`tracfone/subscriptions/${id}/`);
export const fetchAllTracfoneServiceCodes = () =>
  axios.get(`tracfone/plans/service_codes/`);
export const fetchTracfoneSubContract = (sub) =>
  axios.get(`insurance/contracts/`, { params: { tracfone_subscription: sub } });
export const suspendTracfoneSubscription = (id, suspensionReason) =>
  axios.post(`tracfone/subscriptions/${id}/suspend/`, {
    suspension_status: suspensionReason,
  });
export const restoreTracfoneSubscription = (id) =>
  axios.post(`tracfone/subscriptions/${id}/restore/`);
export const fetchUserTracfoneSubs = (userId, page = 1) =>
  axios.get(`tracfone/subscriptions/?user_profile=${userId}&page=${page}`);
export const searchTracfoneSubs = (page, search, page_size) =>
  axios.get(`tracfone/subscriptions/`, {
    params: { ...search, page, page_size },
  });
export const validateTracfonePort = (mdn, service_zip_code) =>
  axios.get(`tracfone/validate_port/`, { params: { mdn, service_zip_code } });
export const validateTracfoneDevice = (device_id) =>
  axios.get(`tracfone/device_eligibility/`, { params: { device_id } });
export const updateTracfoneDevice = (id, device) =>
  axios.post(`tracfone/subscriptions/${id}/update_device/`, device);
export const updateTracfonePort = (id, port) =>
  axios.post(`tracfone/subscriptions/${id}/update_port/`, port);
export const tracfonePortInfo = (id) =>
  axios.get(`tracfone/subscriptions/${id}/port_info/`);
export const submitTracfonePort = (id) =>
  axios.post(`tracfone/subscriptions/${id}/submit_port/`);
export const cancelTracfonePort = (id, data) =>
  axios.post(`tracfone/subscriptions/${id}/cancel_port/`, data);
export const tracfonePortStatus = (id, data) =>
  axios.post(`tracfone/subscriptions/${id}/port_status/`, data);
export const fetchUserTracfoneFilteredSubs = (userId, filters, page = 1) => {
  let url = '';
  if (userId) {
    url = `tracfone/subscriptions/?user_profile=${userId}&page=${page}`;
  } else {
    url = `tracfone/subscriptions/?page=${page}`;
  }
  if (filters) {
    url += `&${filters}`;
  }
  return axios.get(url);
};
export const updateTracfoneStatus = (id) =>
  axios.post(`tracfone/subscriptions/${id}/update_status/`);
export const findTracfoneSubDataUsage = (id, cycle) =>
  axios.get(`tracfone/usage/${id}/?cycle=${cycle}`);
export const updateTracfoneMdn = (id, data) =>
  axios.post(`tracfone/subscriptions/${id}/update_mdn/`, data);
export const generateTracfoneRenewalPurchases = (data) =>
  axios.post(`/purchases/tracfone_renewal/`, data);
export const generateTracfonePlanInvoice = (data) =>
  axios.post(`purchases/tracfone_plan/`, data);
export const fetchTracfoneSubLeases = (subId, page = 1, params) =>
  axios.get(`leases/?tracfone_subscription=${subId}&page=${page}`, {
    params,
  });

// Simo
export const fetchUserSimoFilteredSubs = (userId, filters, page = 1) => {
  let url = '';
  if (userId) {
    url = `simo/subscriptions/?user_profile=${userId}&page=${page}`;
  } else {
    url = `simo/subscriptions/?page=${page}`;
  }
  if (filters) {
    url += `&${filters}`;
  }
  return axios.get(url);
};
export const fetchSimoAccountsList = (params = {}) =>
  axios.get(`simo/accounts_list/?ordering=name&page_size=1000`, { params });
export const createSimoSub = (attrs) =>
  axios.post(`simo/subscriptions/`, attrs);
export const findSimoSub = (id) => axios.get(`simo/subscriptions/${id}/`);
export const fetchAllSimoSubs = (page = 1, ordering = '-id', params = {}) =>
  axios.get(`simo/subscriptions/?page=${page}&ordering=${ordering}`, {
    params,
  });
export const searchSimoSubs = (page = 1, search, page_size) => {
  return axios.get(`simo/subscriptions/`, {
    params: {
      ...search,
      page,
      page_size,
    },
  });
};
export const updateSimoSub = (id, attrs) =>
  axios.patch(`simo/subscriptions/${id}/`, attrs);
export const activateSimoSub = (id, data) =>
  axios.post(`simo/subscriptions/${id}/activate/`, data);
export const updateSimoPlan = (id, plan) =>
  axios.post(`simo/subscriptions/${id}/update_plan/`, plan);
export const simoImmediateRenewal = (id, plan) =>
  axios.post(`/simo/subscriptions/${id}/immediate_renewal/`, plan);
export const fetchSimoPlansByPage = (page = 1, params, page_size) =>
  axios.get(`simo/plans/`, { params: { ...params, page, page_size } });
export const fetchAllSimoPlans = async (params) => {
  let page = 1;
  let isKeepOn = true;
  const response = [];

  while (isKeepOn) {
    const plansResponse = await fetchSimoPlansByPage(
      page,
      params,
      BIG_PAGE_SIZE,
    );
    if (Array.isArray(plansResponse.data.results)) {
      response.push(...plansResponse.data.results);
    }

    if (plansResponse.data.next) {
      page++;
    } else {
      isKeepOn = false;
    }
  }

  return response;
};
export const fetchSimoSubscriptionsOptions = () =>
  axios.options(`simo/subscriptions/`);
export const fetchSimoExpiryOptions = (id) =>
  axios.options(`simo/subscriptions/${id}/expire/`);
export const fetchSimoPlan = (id) => axios.get(`simo/plans/${id}/`);
export const setUpcomingSimoPlan = (subId, { plan_id }) =>
  axios.post(`simo/subscriptions/${subId}/update_upcoming_plan/`, {
    plan_id,
  });
export const fetchSimoSubUpcomingPlan = (subId) =>
  axios.get(`simo/cycles/upcoming/`, { params: { subscription: subId } });
export const toggleSimoAutoRenew = (id) =>
  axios.post(`simo/subscriptions/${id}/toggle_auto_renew/`);
export const suspendSimoSubscription = (id, suspensionReason) =>
  axios.post(`simo/subscriptions/${id}/suspend/`, {
    suspension_status: suspensionReason,
  });
export const suspendSimoSubscriptionOptions = (id) =>
  axios.options(`simo/subscriptions/${id}/suspend/`);
export const restoreSimoSubscription = (id) =>
  axios.post(`simo/subscriptions/${id}/restore/`);
export const updateSimoSubCycleType = (id, attrs) =>
  axios.post(`simo/subscriptions/${id}/update_cycle_type/`, attrs);
export const fetchSimoSubLeases = (subId, page = 1, params) =>
  axios.get(`leases/?simo_subscription=${subId}&page=${page}`, {
    params,
  });
export const fetchSimoUsageBalance = (subId) =>
  axios.get(`simo/subscriptions/${subId}/get_usage/`);
export const generateSimoRenewalPurchases = (data) =>
  axios.post(`/purchases/simo_renewal/`, data);
export const fetchSimoTopUps = (subId, params) =>
  axios.get(`simo/plans/available_top_ups/?simo_subscription=${subId}`, {
    params,
  });
export const addSimoTopUp = (subId, attrs) =>
  axios.post(`simo/subscriptions/${subId}/add_top_up/`, attrs);
export const upgradeSimoTopUp = (payload) =>
  axios.post(`purchases/simo_plan/`, payload);
export const upgradePurchaseSimoPlan = (payload) =>
  axios.post(`purchases/simo_plan/`, payload);

// sql reports
export const fetchSqlReports = (page = 1) =>
  axios.get(`sql_reports/`, { params: { page } });
export const findSqlReport = (id) => axios.get(`sql_reports/${id}/`);
export const findSqlReportOptions = (id) => axios.options(`sql_reports/${id}/`);
export const updateSqlReport = (id, attrs) =>
  axios.patch(`sql_reports/${id}/`, attrs);
export const downloadSqlReport = (id) =>
  axios.post(
    `sql_reports/${id}/download_report/`,
    {},
    { responseType: 'blob' },
  );
export const runSqlReport = (id) => axios.post(`sql_reports/${id}/run_report/`);
export const exportSqlReport = (id) =>
  axios.post(`sql_reports/${id}/export_report/`);
export const findSqlReportQuery = (id) =>
  axios.get(`sql_report_queries/${id}/`);
export const updateSqlReportQuery = (id, attrs) =>
  axios.patch(`sql_report_queries/${id}/`, attrs);
export const downloadSqlReportQuery = (id) =>
  axios.post(
    `sql_report_queries/${id}/download/`,
    {},
    { responseType: 'blob' },
  );
export const exportSqlReportQuery = (id) =>
  axios.post(`sql_report_queries/${id}/export/`);

// analytics
export const fetchUsersAnalytics = () => axios.get(`analytics/users/`);
export const fetchSubscriptionsAnalytics = () =>
  axios.get(`analytics/subscriptions/`);
export const fetchPlansAnalytics = () => axios.get(`analytics/plans/`);

// simo devices
export const fetchAllSimoDevices = (params) =>
  axios.get(`simo/devices/`, { params });
export const findSimoDevice = (id) => axios.get(`simo/devices/${id}/`);
export const createSimoDevice = (attrs) => axios.post(`simo/devices/`, attrs);
export const changeSimoAccount = (deviceId, attrs) =>
  axios.post(`simo/devices/${deviceId}/change_account/`, attrs);
export const fetchSimoDeviceDiagnostic = (deviceId) =>
  axios.get(`simo/devices/${deviceId}/diagnostic_data/`);
export const restartSimoConnection = (deviceId) =>
  axios.post(`simo/devices/${deviceId}/restart_connection/`);
export const restartSimoDevice = (deviceId) =>
  axios.post(`simo/devices/${deviceId}/restart_device/`);
export const setSimoWifiSettings = (deviceId, attrs) =>
  axios.post(`simo/devices/${deviceId}/wifi_settings/`, attrs);
export const sendSimoSignalScan = (deviceId, attrs) =>
  axios.post(`simo/devices/${deviceId}/signal_scan/`, attrs);
export const updateSimoDevice = (id, device) =>
  axios.patch(`simo/devices/${id}/`, device);
export const expireSimoDevice = (deviceId, attrs) =>
  axios.post(`simo/devices/${deviceId}/expire/`, attrs);
export const suspendSimoDevice = (id, suspensionReason) =>
  axios.post(`simo/devices/${id}/suspend/`, {
    suspension_status: suspensionReason,
  });
export const suspendSimoDeviceOptions = (id) =>
  axios.options(`simo/devices/${id}/suspend/`);
export const restoreSimoSDevice = (id) =>
  axios.post(`simo/devices/${id}/restore/`);
export const transferSimoDevice = (id, attrs) =>
  axios.post(`simo/devices/${id}/transfer/`, attrs);
export const cancelSimoScheduledOperation = (id) =>
  axios.post(`scheduled_operations/${id}/cancel/`);
export const simoMassSuspend = (attrs) =>
  axios.post(`simo/devices/mass_suspend/`, attrs);
export const simoMassExpire = (attrs) =>
  axios.post(`simo/devices/mass_expire/`, attrs);
export const simoMassRestore = (attrs) =>
  axios.post(`simo/devices/mass_restore/`, attrs);
export const simoMassAccountChange = (attrs) =>
  axios.post(`simo/devices/mass_account_change/`, attrs);
export const fetchVsimProducts = (deviceId) =>
  axios.get(`simo/devices/${deviceId}/vsim_products/`);
export const changeVsimProduct = (deviceId, attrs) =>
  axios.post(`simo/devices/${deviceId}/change_vsim/`, attrs);

// quick subs
export const fetchQuickSubsOptions = () =>
  axios.options(`quick/subscriptions/`);
export const fetchAllQuickSubs = (page = 1, ordering = '-id', params = {}) =>
  axios.get(`quick/subscriptions/?page=${page}&ordering=${ordering}`, {
    params,
  });
export const createQuickSub = (attrs) =>
  axios.post(`quick/subscriptions/`, attrs);
export const fetchAllQuickInitialsSubs = (page, params, page_size) =>
  axios.get(`quick/subscriptions/?page=${page}`, {
    params: { ...params, page_size },
  });
export const searchQuickSubs = (page = 1, search, page_size) => {
  return axios.get(`quick/subscriptions/`, {
    params: {
      ...search,
      page,
      page_size,
    },
  });
};
export const fetchUserQuickFilteredSubs = (userId, filters, page = 1) => {
  let url = '';
  if (userId) {
    url = `quick/subscriptions/?user_profile=${userId}&page=${page}`;
  } else {
    url = `quick/subscriptions/?page=${page}`;
  }
  if (filters) {
    url += `&${filters}`;
  }
  return axios.get(url);
};
export const findQuickSub = (id) => axios.get(`quick/subscriptions/${id}/`);
export const updateQuickSub = (id, attrs) =>
  axios.patch(`quick/subscriptions/${id}/`, attrs);
export const fetchQuickPortStatus = (id) =>
  axios.get(`quick/subscriptions/${id}/port_status/`);
export const updateQuickPort = (id, port) =>
  axios.post(`quick/subscriptions/${id}/port_update/`, port);
export const quickSubmitPort = (id, port) =>
  axios.post(`quick/subscriptions/${id}/port_submit/`, port);
export const quickInternalPort = (id, port) =>
  axios.post(`quick/subscriptions/${id}/internal_port/`, port);
export const cancelQuickPort = (id, data) =>
  axios.post(`quick/subscriptions/${id}/port_cancel/`, data);
export const transferQuickSubscription = (id, attrs) =>
  axios.post(`quick/subscriptions/${id}/transfer/`, attrs);
export const updateQuickDevice = (id, device) =>
  axios.post(`quick/subscriptions/${id}/update_device/`, device);
export const suspendQuickSubscription = (id, suspensionReason, expiryType) =>
  axios.post(`quick/subscriptions/${id}/suspend/`, {
    suspension_status: suspensionReason,
    expiry_type: expiryType,
  });
export const suspendQuickSubscriptionOptions = (id) =>
  axios.options(`quick/subscriptions/${id}/suspend/`);
export const restoreQuickSubscription = (id) =>
  axios.post(`quick/subscriptions/${id}/restore/`);
export const reactivateQuickSubscription = (id) =>
  axios.post(`quick/subscriptions/${id}/reactivate/`);
export const expireQuickSubscription = (id, attrs) =>
  axios.post(`quick/subscriptions/${id}/expire/`, attrs);
export const updateQuickPlan = (id, plan) =>
  axios.post(`quick/subscriptions/${id}/update_plan/`, plan);
export const lockQuickPlan = (id, locked) =>
  axios.put(`quick/subscriptions/${id}/plan_locked/`, { plan_locked: locked });
export const fetchQuickPlansByPage = (page = 1, params, page_size) =>
  axios.get(`quick/plans/`, { params: { ...params, page, page_size } });
export const fetchAllQuickPlans = async (params) => {
  let page = 1;
  let isKeepOn = true;
  const response = [];

  while (isKeepOn) {
    const plansResponse = await fetchQuickPlansByPage(
      page,
      params,
      BIG_PAGE_SIZE,
    );
    if (Array.isArray(plansResponse.data.results)) {
      response.push(...plansResponse.data.results);
    }

    if (plansResponse.data.next) {
      page++;
    } else {
      isKeepOn = false;
    }
  }

  return response;
};
export const fetchQuickPlan = (id) => axios.get(`quick/plans/${id}/`);
export const upgradePurchaseQuickPlan = (payload) =>
  axios.post(`purchases/quick_plan/`, payload);
export const fetchQuickCurrentCycle = (subscription) =>
  axios.get('/quick/cycles/current/', {
    params: {
      subscription,
    },
  });
export const fetchQuickSubUsage = (subId) =>
  axios.get(`quick/subscriptions/${subId}/get_usage/`);
export const fetchQuickSubUpcomingPlan = (subId) =>
  axios.get(`quick/cycles/upcoming/`, { params: { subscription: subId } });
export const setUpcomingQuickPlan = (subId, { plan_id, plan_lock }) =>
  axios.post(`/quick/subscriptions/${subId}/update_upcoming_plan/`, {
    plan_id,
    plan_locked: plan_lock,
  });
export const quickChangeVoicemailPin = (subId, attrs) =>
  axios.post(`quick/subscriptions/${subId}/change_voicemail_pin`, attrs);
export const quickDeleteVoicemailPin = (subId) =>
  axios.post(`quick/subscriptions/${subId}/delete_voicemail_pin/`);
export const changeQuickCallerId = (id, attrs) =>
  axios.post(`quick/subscriptions/${id}/change_caller_id/`, attrs);
export const fetchQuickServiceCodes = (id) =>
  axios.get(`quick/subscriptions/${id}/service_codes/`);
export const updateQuickServiceCodes = (id, attrs) =>
  axios.put(`quick/subscriptions/${id}/service_codes/`, attrs);
export const changeQuickMdn = (id, attrs) =>
  axios.post(`quick/subscriptions/${id}/change_mdn/`);
export const fetchQuickSubContract = (quick_subscription) =>
  axios.get('insurance/contracts/', { params: { quick_subscription } });
export const activateQuickSub = (id, data) =>
  axios.post(`quick/subscriptions/${id}/activate/`, data);
export const sendQuickVerificationCode = (id, attrs) =>
  axios.post(`quick/subscriptions/${id}/request_auth_code/`, attrs);
export const verifyQuickVerificationCode = (id, attrs) =>
  axios.post(`quick/subscriptions/${id}/verify_auth_code/`, attrs);
export const quickCheckCoverage = (attrs) =>
  axios.post(`quick/check_coverage/`, attrs);
export const quickSimEligibility = (attrs) =>
  axios.post(`quick/sim_eligibility/`, attrs);
export const quickDeviceEligibility = (attrs) =>
  axios.post(`quick/device_eligibility/`, attrs);
export const quickValidatePort = (attrs) =>
  axios.post(`quick/validate_port/`, attrs);
export const fetchAllQuickServiceCodes = () =>
  axios.get('quick/service_codes/');
export const fetchQuickVendorServiceCodes = (vendor) =>
  axios.get(`quick/service_codes/?vendor=${vendor}`);
export const fetchQuickRunningActions = (id) =>
  axios.get(`quick/subscriptions/${id}/get_async_actions/`);
export const fetchQuickPortOuts = (params) =>
  axios.get(`quick/portout_list/`, { params });
export const quickPortOutResolve = (subId, attrs) =>
  axios.post(`/quick/subscriptions/${subId}/release_port/`, attrs);
export const fetchQuickCarrierRequests = (subId, params) =>
  axios.get(`/quick/subscriptions/${subId}/carrier_requests/`, { params });
export const quickSetServiceAddress = (subId, attrs) =>
  axios.post(`/quick/subscriptions/${subId}/set_address/`, attrs);
export const fetchQuickPortData = (subId, params) =>
  axios.get(`/quick/subscriptions/${subId}/quick_port_data/`, { params });
export const fetchQuickMdnHistory = (subId) =>
  axios.get(`/quick/subscriptions/${subId}/mdn_histories/`);
export const quickUpdateSubStatus = (subId) =>
  axios.post(`/quick/subscriptions/${subId}/sync_status/`);
export const quickMassSuspend = (attrs) =>
  axios.post(`quick/subscriptions/mass_suspend/`, attrs);
export const quickMassExpire = (attrs) =>
  axios.post(`quick/subscriptions/mass_expire/`, attrs);
export const quickMassRestore = (attrs) =>
  axios.post(`quick/subscriptions/mass_restore/`, attrs);
export const quickMassActivation = (attrs) =>
  axios.post(`quick/subscriptions/mass_activation/`, attrs, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
